import {
  useEffect,
  useState,
  type ComponentType,
  type ContextType
} from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  useNavigate,
  useOutletContext,
  useParams
} from 'react-router-dom';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import CancelModal from '@/app/common/components/CancelModal';
import AiInsufficientBalanceModal, {
  AI_WALLET_INSUFFICIENT_CODE
} from '@/app/common/components/AiInsufficientBalanceModal';
import {
  useGetContentHistoryQuery,
  useSendContentAnalysisMutation,
  useDeleteHistoryRecordMutation
} from '@/app/ContentTools/api/aiContentAnalysisApi';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import { AiContentHistoryColumns } from '@/app/ContentTools/config/aiContentToolsColumnConfig';
import { RemoveAiContentContext } from '@/context/Context';
import { getAiWarningMessage } from '@/app/common/validators/aiMessages';
import type { AiContentHistoryRow } from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import AiAnalysisSearchBox from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSearchBox';
import { aiAnalysisDetailsPath } from '@/app/ContentTools/utils/aiAnalysisPaths';

type BreadcrumbItem = {
  text: string;
  href: string;
};

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type ApiErrorResponse = {
  status?: number;
  data?: {
    code?: number;
  };
};

type AgGridWrapperProps = {
  height: string;
  columns: unknown[];
  data: unknown[];
  settings?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const AiContentAnalysis = () => {
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { domain_slug, domain } = useParams();
  const domainSlug = domain_slug || '';
  const currentDomain = domain || '';
  const navigate = useNavigate();
  const [remId, setRemId] = useState('');
  const [remKeyword, setRemKeyword] = useState('');
  const [showRem, setShowRem] = useState(false);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] =
    useState(false);

  useEffect(() => {
    setBreadcrumbData([{ text: 'تاریخچه', href: '#' }]);
  }, [setBreadcrumbData]);

  const { data: historyData, isLoading: isLoadingHistory } =
    useGetContentHistoryQuery();

  const [sendContentAnalysis] = useSendContentAnalysisMutation();
  const [deleteHistoryRecord] = useDeleteHistoryRecordMutation();

  const removeAiContentContextValue = {
    id: remId,
    setId: setRemId,
    keyword: remKeyword,
    setKeyword: setRemKeyword,
    show: showRem,
    setShow: setShowRem
  } as ContextType<typeof RemoveAiContentContext>;

  const handleReRun = async (row: AiContentHistoryRow) => {
    if (!row?.url || !row?.keyword) {
      errorMessage('اطلاعات کافی برای جستجوی مجدد وجود ندارد');
      return;
    }
    try {
      const res = await sendContentAnalysis({
        domain_slug: domainSlug,
        url: row.url,
        keyword: row.keyword,
        goal: row.goal || 'تحلیل مجدد خودکار محتوا با استفاده از تنظیمات قبلی',
        analysis_types:
          row.analysis_types && row.analysis_types.length > 0
            ? row.analysis_types
            : ['seo_analysis', 'ai_detection']
      }).unwrap();
      if (res?.code === 200) {
        if (res?.data?.error?.code !== null) {
          errorMessage(res?.data?.error?.message || 'در ارتباط با سرویس تحلیل AI خطایی رخ داد.');
          return;
        } else {
          successMessage('جستجوی مجدد با موفقیت ثبت شد');
          if (res?.data?.id) {
            navigate(
              aiAnalysisDetailsPath(
                'overview',
                domainSlug,
                currentDomain,
                res.data.id
              )
            );
          }
        }
      } else {
        if (res?.code === AI_WALLET_INSUFFICIENT_CODE) {
          setShowInsufficientBalanceModal(true);
          return;
        }
        const warningText = getAiWarningMessage(res?.code);
        if (warningText) {
          warningMessage(warningText);
          return;
        }

        errorMessage(res?.msg || 'در ارتباط با سرویس تحلیل AI خطایی رخ داد.');
      }
    } catch (err) {
      const error = err as ApiErrorResponse;
      if (error?.data?.code === AI_WALLET_INSUFFICIENT_CODE) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      const warningText = getAiWarningMessage(error?.data?.code);
      if (warningText) {
        warningMessage(warningText);
        return;
      }
      errorMessage(
        error?.data?.msg || 'در جستجوی مجدد خطایی رخ داد'
      );
    }
  };

  return (
    <RemoveAiContentContext.Provider
      value={removeAiContentContextValue}
    >
        <AiAnalysisSearchBox />

        <Row>
          <Col className="for-style">
            {!isLoadingHistory ? (
              <TypedAgGridWrapper
                height="600px"
                columns={AiContentHistoryColumns(domainSlug, currentDomain, {
                  onReRun: handleReRun
                })}
                data={historyData || []}
                settings={{ pagination: true }}
              />
            ) : (
              <AgGridSkeleton height="600px" />
            )}
          </Col>
        </Row>
        <CancelModal
          setShow={setShowRem}
          show={showRem}
          submitTitle="آره، حذف بشه"
          title="حذف تحلیل محتوا با AI"
          submitFunc={() => {
            deleteHistoryRecord({ id: remId })
              .unwrap()
              .then(res => {
                if (res?.code === 200) {
                  successMessage('حذف تحلیل محتوا با AI با موفقیت انجام شد');
                } else {
                  errorMessage(res?.msg || 'در حذف تحلیل محتوا با AI خطایی رخ داد');
                }
              })
              .catch(() => {
                errorMessage('در حذف تحلیل محتوا با AI خطایی رخ داد');
              });
          }}
          desc={`آیا مطمئن هستید می‌خواهید تحلیل مربوط به "${decodeURIComponent(
            remKeyword || ''
          )}" را حذف کنید؟`}
        />
        <AiInsufficientBalanceModal
          show={showInsufficientBalanceModal}
          onHide={() => setShowInsufficientBalanceModal(false)}
        />
        <style>
          {`
          .for-style .ag-row-focus {
              z-index: 2;
        }`}
        </style>
    </RemoveAiContentContext.Provider>
  );
};

export default AiContentAnalysis;
